import {
  MonthSummaryCards,
  type MonthSummaryCard,
} from '@/components/month-summary-cards'
import { ToggleGroup, ToggleGroupItem } from '@/components'
import { SVGIcon } from '@/components_deprecated'
import { format } from '@/utils/date-fns'
import { Module, ModuleOptionsValue } from '@/modules/companies/consts'
import { useModuleConfig } from '@/utils/hooks'
import { Icon } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import {
  formatSecondsAsHhmm,
  type SalaryMonthSummary,
} from './utils/aggregate-month-summary'

export type SalarySumScope = 'all' | 'approved'

interface SalaryMonthSummaryCardsProps {
  month: Date
  rangeStart: Date
  rangeEnd: Date
  summary: SalaryMonthSummary
  loading?: boolean
  collapsed?: boolean
  onToggleCollapsed?: () => void
  /**
   * When provided, render an "All / Approved" scope toggle in the title
   * row. Omit when the company has no approval flow (both sums identical).
   */
  sumScope?: SalarySumScope
  onSumScopeChange?: (scope: SalarySumScope) => void
}

/**
 * Salary-view adapter: maps an aggregated `SalaryMonthSummary` to the
 * shared `MonthSummaryCards` primitive.
 */
export const SalaryMonthSummaryCards: React.FC<
  SalaryMonthSummaryCardsProps
> = ({
  month,
  rangeStart,
  rangeEnd,
  summary,
  loading,
  collapsed,
  onToggleCollapsed,
  sumScope,
  onSumScopeChange,
}) => {
  const translations = useTranslate({
    sumForMonth: ['activities.sum-for-month-title', { month: '' }],
    totalWork: 'common.total-work',
    pause: 'common.pause',
    paidRest: 'activities.paid-pause',
    showSums: 'common.show-sums',
    hideSums: 'common.hide-sums',
    all: 'common.all',
    approved: 'common.approved',
    driving: 'activities.driving',
    otherWork: 'common.other-work',
    overtime: 'activities.sum-overtime',
    available: 'activities.availability-time',
    scopeInfo: ['salary.month-summary-scope-info', { start: '', end: '' }],
    totalWorkInfo: 'salary.month-summary-total-work-info',
  })

  // Which summary cards to render is company-configurable, mirroring the
  // Activities-overview summary toggles. Total work is the headline sum
  // and is always shown.
  const { getModuleOptionStatus } = useModuleConfig(Module.SALARY)
  const show = {
    driving: getModuleOptionStatus(
      ModuleOptionsValue.SALARY_SUMMARY_SHOW_DRIVING
    ),
    otherWork: getModuleOptionStatus(
      ModuleOptionsValue.SALARY_SUMMARY_SHOW_OTHER_WORK
    ),
    availability: getModuleOptionStatus(
      ModuleOptionsValue.SALARY_SUMMARY_SHOW_AVAILABILITY_TIME
    ),
    pause: getModuleOptionStatus(ModuleOptionsValue.SALARY_SUMMARY_SHOW_PAUSE),
    paidRest: getModuleOptionStatus(
      ModuleOptionsValue.SALARY_SUMMARY_SHOW_PAID_REST
    ),
    overtime: getModuleOptionStatus(
      ModuleOptionsValue.SALARY_SUMMARY_SHOW_OVERTIME
    ),
  }

  // Overtime is only meaningful as a single-month sum when the user
  // computes overtime per day (no week/month overtime spanning months).
  const showOvertime = show.overtime && summary.overtimeIsDayOnly

  const cards: MonthSummaryCard[] = [
    {
      key: 'totalWork',
      label: translations.totalWork,
      tooltip: translations.totalWorkInfo,
      value: formatSecondsAsHhmm(summary.totalWorkSeconds),
      iconBg: '#344054',
      icon: (
        <Icon icon="business-time" fixedWidth size="2.36em" color="white" />
      ),
    },
  ]

  if (show.driving) {
    cards.push({
      key: 'driving',
      label: translations.driving,
      value: formatSecondsAsHhmm(summary.drivingSeconds),
      iconBg: '#399D26',
      icon: <SVGIcon icon="truck" />,
    })
  }
  if (show.otherWork) {
    cards.push({
      key: 'otherWork',
      label: translations.otherWork,
      value: formatSecondsAsHhmm(summary.otherWorkSeconds),
      iconBg: '#ffb600',
      icon: <Icon icon="briefcase" fixedWidth size="2.92em" />,
    })
  }
  if (show.availability) {
    cards.push({
      key: 'availability',
      label: translations.available,
      value: formatSecondsAsHhmm(summary.availabilitySeconds),
      iconBg: '#00c7b1',
      icon: (
        <Icon
          icon="hand-holding"
          fixedWidth
          translateY="-0.8rem"
          size="2.622em"
        />
      ),
    })
  }
  if (show.pause) {
    cards.push({
      key: 'pause',
      label: translations.pause,
      value: formatSecondsAsHhmm(summary.pauseSeconds),
      iconBg: '#4C9CF1',
      icon: <Icon icon="pause" fixedWidth size="2.92em" />,
    })
  }
  if (show.paidRest) {
    cards.push({
      key: 'paidRest',
      label: translations.paidRest,
      value: formatSecondsAsHhmm(summary.paidRestSeconds),
      iconBg: '#b39ddb',
      icon: <Icon icon="mug-hot" fixedWidth size="2.92em" />,
    })
  }
  if (showOvertime) {
    cards.push({
      key: 'overtime',
      label: translations.overtime,
      value: formatSecondsAsHhmm(summary.overtimeSeconds),
      iconBg: '#fabbce',
      icon: <SVGIcon icon="clock" />,
    })
  }

  return (
    <MonthSummaryCards
      title={translations.sumForMonth({ month: format(month, 'MMMM') })}
      cards={cards}
      loading={loading}
      titleTooltip={translations.scopeInfo({
        start: format(rangeStart, 'd. MMM yyyy'),
        end: format(rangeEnd, 'd. MMM yyyy'),
      })}
      collapsed={collapsed}
      onToggleCollapsed={onToggleCollapsed}
      toggleLabel={collapsed ? translations.showSums : translations.hideSums}
      titleExtra={
        sumScope && onSumScopeChange ? (
          <ToggleGroup
            type="single"
            variant="outline"
            size="sm"
            value={sumScope}
            onValueChange={value => {
              // Radix emits '' when the active item is clicked again; keep
              // the current selection instead of clearing it.
              if (value === 'all' || value === 'approved') {
                onSumScopeChange(value)
              }
            }}
          >
            <ToggleGroupItem value="all">
              <span className="translate-y-px">{translations.all}</span>
            </ToggleGroupItem>
            <ToggleGroupItem value="approved">
              <span className="translate-y-px">{translations.approved}</span>
            </ToggleGroupItem>
          </ToggleGroup>
        ) : undefined
      }
    />
  )
}
